/**
 * Attaching a file to an outgoing mail — the wire shape for `mail.upload_attachment`.
 *
 * ---- Why this exists now ----
 *
 * `ComposeModal`'s Attach button and drop zone have been disabled behind `liveAvailability.ts`,
 * because no endpoint was known and `useUploadFile` posted to a mock-only `/api/v1/files`. The
 * method was there all along; only its shape was unmeasured. Probed live 2026-09-09:
 *
 *     POST mail.upload_attachment  {file_name, file_data: <base64>, is_private}
 *       → 200 {file_url: "/private/files/bosun-probe-private419503.txt", file_name: "…"}
 *
 * That closes ask `A4`.
 *
 * ---- PRIVATE, always ----
 *
 * `is_private: 1` (Akhil, 2026-09-09). A public upload lands at `/files/<name><hash>.ext`, which
 * anybody who reaches the origin can fetch without a permission check — and these are customer and
 * supplier documents. Verified: private uploads come back under `/private/files/`, which Frappe
 * permission-checks on every read. It is not a parameter the caller gets to choose.
 */

import { toAttachmentKind } from './api/useMail'
import type { UploadedFile } from './types'

export interface WireUploadAttachment {
  file_url?: unknown
  file_name?: unknown
}

export interface UploadAttachmentBody {
  file_name: string
  /** Bare base64 — no `data:…;base64,` prefix. See `toUploadBody`. */
  file_data: string
  is_private: 1
}

/**
 * `FileReader.readAsDataURL` produces `data:<mime>;base64,<payload>`, and the server wants only
 * the payload — sending the whole thing stores a file whose first bytes are the word "data".
 * Tolerates a string that is already bare, so a caller that strips it itself is not punished.
 */
export function toUploadBody(fileName: string, dataUrlOrBase64: string): UploadAttachmentBody {
  const comma = dataUrlOrBase64.indexOf(',')
  const isDataUrl = dataUrlOrBase64.startsWith('data:') && comma >= 0
  return {
    file_name: fileName,
    file_data: isDataUrl ? dataUrlOrBase64.slice(comma + 1) : dataUrlOrBase64,
    is_private: 1,
  }
}

/** `1.2 MB` / `13 KB` / `840 B` — what the attachment chip shows, matching `MailAttachment.size`. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${Math.round(bytes)} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * The upload's id swapped for the `File` DOCNAME.
 *
 * `mail.send_email` runs `get_doc("File", <each attachment>)` — measured 2026-09-22 from the
 * Error Log of a failed forward — so a url is a 500, not an attachment. `upload_attachment`
 * answers `{file_url, file_name}` and no docname, so it is read back from the record the upload
 * just created (`D44`: returning `name` would remove this round trip).
 *
 * A failed lookup leaves the url in place rather than failing the upload: the file IS stored,
 * the chip is real, and the send reports what the send could not do.
 */
export async function withFileDocname(file: UploadedFile, lookup: (fileUrl: string) => Promise<string>): Promise<UploadedFile> {
  try {
    const docname = (await lookup(file.id)).trim()
    return docname === '' ? file : { ...file, id: docname }
  } catch {
    return file
  }
}

export function toUploadedFile(wire: WireUploadAttachment, bytes: number): UploadedFile {
  const fileUrl = typeof wire.file_url === 'string' ? wire.file_url.trim() : ''
  // Without it there is nothing to attach — better to fail the upload than to add a chip that
  // points nowhere and is discovered only when the customer reports an empty mail.
  if (fileUrl === '') throw new Error('upload_attachment returned no file_url')

  const name = typeof wire.file_name === 'string' && wire.file_name ? wire.file_name : fileUrl.split('/').pop() || 'attachment'
  return {
    // The url until `withFileDocname` resolves the docname the SEND references — see above.
    id: fileUrl,
    name,
    // Classified from the NAME, so `.xlsm` gets a spreadsheet icon rather than a generic one.
    kind: toAttachmentKind(name, ''),
    size: humanSize(bytes),
    preview_url: fileUrl,
  }
}
